// Kitchen Assist — matching logic for the in-app help overlay.
//
// The cooking screen lights up words in a task's text that have a glossary
// entry at cookpit.net/assist. Matching must be case- AND accent-insensitive
// so "Sauté", "SAUTE" and "saute" all resolve to the same entry, while the
// task panel still shows whatever the recipe authored.
//
// The normalised key is the single canonical token used end to end: it is
// the membership-test key, the cache key, and the word fetched from the
// proxy (the published filenames are accent-free, and the proxy is
// case-insensitive, so the folded form always resolves).

// Unicode combining diacritical marks (U+0300–U+036F), stripped after NFD.
const COMBINING_MARKS = /[\u0300-\u036f]/g;

/**
 * Fold a token to its canonical comparison key: Unicode NFD decomposition,
 * strip the combining marks (ô→o, é→e), lower-case. Note NFD does not
 * decompose ligatures (œ, æ); that is out of scope until a help word needs
 * one (README §12.2).
 */
export function normalizeAssistKey(s: string): string {
  return s.normalize('NFD').replace(COMBINING_MARKS, '').toLowerCase();
}

/** Resolves a task token to the canonical help word, or null if none. */
export interface AssistMatcher {
  /** The canonical (folded) help word for a token, or null if no entry. */
  match(token: string): string | null;
  /** Number of distinct help words known. */
  readonly size: number;
}

/**
 * Build a matcher over the glossary's word list. Words are folded into the
 * canonical keyspace; `match` folds the incoming token the same way and
 * returns the canonical key when it is a member.
 */
export function buildAssistMatcher(words: readonly string[]): AssistMatcher {
  const keys = new Set<string>();
  for (const w of words) {
    const k = normalizeAssistKey(w);
    if (k) keys.add(k);
  }
  return {
    match(token: string): string | null {
      const k = normalizeAssistKey(token);
      return k && keys.has(k) ? k : null;
    },
    get size() {
      return keys.size;
    },
  };
}
